import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Smoothly counts up to `value` whenever it changes.
function useCountUp(value, duration = 900) {
  const [display, setDisplay] = useState(value ?? 0);
  const prev = useRef(value ?? 0);

  useEffect(() => {
    if (value == null) return;
    const from = prev.current;
    const to = value;
    if (from === to) {
      setDisplay(to);
      return;
    }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else prev.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return display;
}

export const LiveCounter = ({ count, dark = false, testId = "live-counter" }) => {
  const display = useCountUp(count);

  if (count == null) return null;

  const base = dark
    ? "border-white/15 bg-white/5 text-slate-200"
    : "border-slate-200 bg-white/70 text-slate-600";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.6 }}
      className={`inline-flex items-center gap-2.5 rounded-full border px-4 py-2 text-sm font-medium backdrop-blur ${base}`}
      data-testid={testId}
      aria-live="polite"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span
          className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
          style={{ backgroundColor: "var(--action-green)" }}
        />
        <span
          className="relative inline-flex h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: "var(--action-green)" }}
        />
      </span>
      <span>
        <AnimatePresence mode="popLayout">
          <motion.span
            key={display}
            className={`font-display font-extrabold tabular-nums ${
              dark ? "text-white" : "text-slate-900"
            }`}
          >
            {display.toLocaleString("en-IN")}
          </motion.span>
        </AnimatePresence>{" "}
        neighbours already waiting in Bhubaneswar
      </span>
    </motion.div>
  );
};

export default LiveCounter;
